import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { CheckCircle, Clock, Clipboard, List, Shield, Zap } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FoundersLaunchPage = () => {
  // State
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [config, setConfig] = useState(null);
  const [activities, setActivities] = useState([]);
  const [countdown, setCountdown] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: ''
  });

  useEffect(() => {
    fetchConfig();
    fetchActivities();
    
    // Refresh activities every 30 seconds
    const interval = setInterval(fetchActivities, 30000);
    return () => clearInterval(interval);
  }, []);

  // Countdown timer
  useEffect(() => {
    if (!config?.countdown_enabled || !config?.countdown_end) return;
    
    const updateCountdown = () => {
      const end = new Date(config.countdown_end);
      const now = new Date();
      const diff = end - now;
      
      if (diff <= 0) {
        setCountdown('Time has passed');
        return;
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      
      setCountdown(`${days}d ${hours}h ${minutes}m`);
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 60000);
    return () => clearInterval(interval);
  }, [config]);

  const fetchConfig = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/public/founders/config`);
      const data = await response.json();
      setConfig(data);
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  const fetchActivities = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/public/access/activities?limit=10`);
      const data = await response.json();
      if (data.ok) {
        setActivities(data.activities || []);
      }
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formPayload = new FormData();
      formPayload.append('full_name', formData.full_name);
      formPayload.append('email', formData.email);
      formPayload.append('phone', formData.phone);

      const response = await fetch(`${BACKEND_URL}/api/public/founders/request`, {
        method: 'POST',
        body: formPayload
      });

      const result = await response.json();

      if (result.success) {
        setSubmitted(true);
        // Track analytics
        if (window.gtag) {
          window.gtag('event', 'form_submit', { success: true });
        }
      } else {
        toast.error(result.error || 'Failed to submit request');
      }
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('lead-form')?.scrollIntoView({ behavior: 'smooth' });
    // Track analytics
    if (window.gtag) {
      window.gtag('event', 'cta_click', { section: 'hero' });
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F3E7D3] p-6">
        <Card className="max-w-2xl w-full p-8 border-2 border-[#1F4D3A]">
          <div className="text-center">
            <CheckCircle className="h-20 w-20 text-[#1F4D3A] mx-auto mb-6" />
            <h2 className="text-3xl font-extrabold text-[#111111] mb-4">
              Position Recorded
            </h2>
            <p className="text-lg text-[#232323] mb-4 leading-relaxed">
              You're now in the system.<br />
              If access is available, next steps will be sent.
            </p>
            <p className="text-base text-[#6B6B6B]">
              <strong>Take your time.</strong> This system rewards action, not urgency.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F3E7D3]">
      {/* SECTION 1: HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#F3E7D3] to-[#E8DCC8] py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-[1120px]">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-[#111111] mb-6 leading-tight tracking-tight">
              This Is the Year Everything Changes.
            </h1>
            <p className="text-xl md:text-2xl text-[#232323] mb-2 max-w-3xl mx-auto leading-relaxed">
              Not because you hoped harder —
            </p>
            <p className="text-xl md:text-2xl text-[#232323] mb-8 max-w-3xl mx-auto leading-relaxed">
              but because you finally chose a system.
            </p>
          </div>

          {/* Countdown Bar */}
          {config?.countdown_enabled && (
            <div className="max-w-2xl mx-auto mb-8">
              <div className="text-center mb-3">
                <p className="text-sm font-semibold text-[#E46A1A] uppercase tracking-wide">
                  Countdown to New Life
                </p>
              </div>
              <div className="bg-[#111111] rounded-full h-3 overflow-hidden mb-2">
                <div 
                  className="bg-[#E46A1A] h-full transition-all duration-1000 ease-linear"
                  style={{ width: '67%' }}
                ></div>
              </div>
              <div className="text-center">
                <p className="text-sm text-[#6B6B6B]">
                  {countdown || 'Loading...'}
                </p>
              </div>
              <p className="text-sm text-center text-[#232323] mt-3 max-w-lg mx-auto">
                When the clock runs out, positions move forward.<br />
                The system does not pause.
              </p>
            </div>
          )}

          {/* Primary CTA */}
          <div className="text-center mb-4">
            <Button
              onClick={scrollToForm}
              className="bg-[#E46A1A] hover:bg-[#D05A0A] text-white px-12 py-6 text-lg font-bold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
            >
              Request Access Now
            </Button>
          </div>
          <p className="text-sm text-center text-[#6B6B6B] max-w-md mx-auto">
            Access is granted in order. Waiting moves you back.
          </p>
        </div>
      </section>

      {/* SECTION 2: VIDEO CENTERPIECE */}
      <section className="bg-[#111111] py-16 md:py-20">
        <div className="container mx-auto px-4 md:px-6 max-w-[1120px]">
          <div className="max-w-4xl mx-auto">
            <div className="relative aspect-video bg-[#232323] rounded-2xl overflow-hidden mb-8 flex items-center justify-center">
              {/* Placeholder for video - replace with actual video embed */}
              <div className="text-center p-8">
                <p className="text-2xl md:text-3xl font-bold text-white mb-4">Most people wait.</p>
                <p className="text-xl text-[#F3E7D3] mb-4">Waiting feels safe.</p>
                <p className="text-xl text-[#F3E7D3] mb-4">But position decides outcomes.</p>
                <p className="text-2xl font-bold text-[#E46A1A] mb-4">This is the year everything changes.</p>
                <p className="text-xl text-white">Get on the list.</p>
              </div>
              {/* When you have video, replace above with:
              <video
                autoPlay
                muted
                loop
                playsInline
                controls
                className="w-full h-full object-cover"
                poster="/path/to/poster.jpg"
              >
                <source src="/path/to/touch0_peak_interest.mp4" type="video/mp4" />
              </video>
              */}
            </div>
            <div className="text-center">
              <Button
                onClick={scrollToForm}
                className="bg-[#E46A1A] hover:bg-[#D05A0A] text-white px-10 py-5 text-lg font-bold rounded-lg shadow-lg"
              >
                Get on the List
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3: LIVE ACTIVITY CAROUSEL */}
      {activities.length > 0 && (
        <section className="bg-[#F3E7D3] py-12 md:py-16 overflow-hidden">
          <div className="container mx-auto px-4 md:px-6 max-w-[1120px]">
            <h2 className="text-2xl md:text-3xl font-extrabold text-[#111111] text-center mb-8">
              Live Movement
            </h2>
            <div className="relative">
              <div className="flex space-x-4 overflow-x-auto pb-4 scrollbar-hide">
                {activities.map((activity, index) => (
                  <Card key={index} className="min-w-[280px] bg-white border-[#E46A1A] border flex-shrink-0">
                    <CardContent className="p-4">
                      <p className="font-semibold text-[#111111] text-sm mb-1">
                        {activity.display_name} entered the ledger
                      </p>
                      <p className="text-xs text-[#6B6B6B]">{activity.time_ago}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* SECTION 4: HOW ACCESS WORKS */}
      <section className="bg-white py-16 md:py-20">
        <div className="container mx-auto px-4 md:px-6 max-w-[1120px]">
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#111111] text-center mb-12">
            How Access Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Clipboard, title: 'Request Access', body: 'Enter real info to preserve fairness and order.' },
              { icon: List, title: 'Position Logged', body: 'Order is recorded. Position matters.' },
              { icon: Shield, title: 'Verification Step', body: 'If access is available, instructions are sent.' },
              { icon: Zap, title: 'System Activated', body: 'Follow steps. No guessing.' }
            ].map((item, index) => (
              <Card key={index} className="text-center p-6 border-2 border-[#F3E7D3] hover:border-[#E46A1A] transition-colors">
                <CardContent className="p-0">
                  <item.icon className="h-12 w-12 text-[#E46A1A] mx-auto mb-4" />
                  <h3 className="font-bold text-lg text-[#111111] mb-2">{item.title}</h3>
                  <p className="text-sm text-[#6B6B6B]">{item.body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5: LEAD CAPTURE FORM */}
      <section id="lead-form" className="bg-[#F3E7D3] py-16 md:py-20">
        <div className="container mx-auto px-4 md:px-6 max-w-[700px]">
          <Card className="border-2 border-[#111111] shadow-2xl">
            <CardContent className="p-8 md:p-12">
              <h2 className="text-3xl md:text-4xl font-extrabold text-[#111111] text-center mb-4">
                Enter the Ledger
              </h2>
              <p className="text-center text-[#232323] mb-8 leading-relaxed">
                Early positioning requires real contact information.<br />
                This system rewards intention, not curiosity.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-[#111111] mb-2">
                    Full Name *
                  </label>
                  <Input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                    required
                    className="border-2 border-[#232323] focus:border-[#E46A1A] text-lg py-6"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#111111] mb-2">
                    Email Address *
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    className="border-2 border-[#232323] focus:border-[#E46A1A] text-lg py-6"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#111111] mb-2">
                    Phone Number
                  </label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="border-2 border-[#232323] focus:border-[#E46A1A] text-lg py-6"
                    placeholder="+1 (555) 123-4567"
                  />
                  <p className="text-xs text-[#6B6B6B] mt-1">
                    Used only for access instructions. No spam.
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#E46A1A] hover:bg-[#D05A0A] text-white py-6 text-xl font-bold rounded-lg shadow-lg"
                >
                  {loading ? 'Submitting...' : 'Secure My Position'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* SECTION 6: CONSEQUENCE BLOCK */}
      <section className="bg-white py-16 md:py-20">
        <div className="container mx-auto px-4 md:px-6 max-w-[900px]">
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#111111] text-center mb-8">
            What Happens If You Wait
          </h2>
          <div className="space-y-4 max-w-2xl mx-auto">
            {[
              'Positions move forward without you',
              'Access windows close',
              'Priority is given to action-takers'
            ].map((item, index) => (
              <div key={index} className="flex items-start space-x-3 p-4 bg-[#F3E7D3] rounded-lg">
                <div className="w-2 h-2 bg-[#E46A1A] rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-lg text-[#111111]">{item}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-[#6B6B6B] mt-6 max-w-lg mx-auto">
            The clock doesn't pressure you. It simply keeps moving.
          </p>
        </div>
      </section>

      {/* SECTION 7: WHO THIS IS FOR / NOT FOR */}
      <section className="bg-[#F3E7D3] py-16 md:py-20">
        <div className="container mx-auto px-4 md:px-6 max-w-[1120px]">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Who this is for */}
            <Card className="border-2 border-[#1F4D3A] bg-white">
              <CardContent className="p-8">
                <h3 className="text-2xl font-extrabold text-[#1F4D3A] mb-6 text-center">
                  This is for people who:
                </h3>
                <div className="space-y-3">
                  {['Want structure', 'Follow systems', 'Are done starting over'].map((item, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <CheckCircle className="h-6 w-6 text-[#1F4D3A] flex-shrink-0" />
                      <p className="text-[#111111] font-medium">{item}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Who this is NOT for */}
            <Card className="border-2 border-[#E46A1A] bg-white">
              <CardContent className="p-8">
                <h3 className="text-2xl font-extrabold text-[#E46A1A] mb-6 text-center">
                  This is not for people who:
                </h3>
                <div className="space-y-3">
                  {['Wait for perfect timing', 'Need constant motivation', 'Avoid making decisions'].map((item, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className="h-6 w-6 border-2 border-[#E46A1A] rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-[#E46A1A] font-bold">✕</span>
                      </div>
                      <p className="text-[#111111] font-medium">{item}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* SECTION 8: FOOTER */}
      <footer className="bg-[#111111] text-white py-12">
        <div className="container mx-auto px-4 md:px-6 max-w-[1120px]">
          <div className="text-center space-y-4">
            <div className="flex justify-center space-x-8 text-sm">
              <a href="/privacy" className="hover:text-[#E46A1A] transition-colors">Privacy Policy</a>
              <a href="/terms" className="hover:text-[#E46A1A] transition-colors">Terms & Conditions</a>
              <a href="/disclaimer" className="hover:text-[#E46A1A] transition-colors">Income Disclaimer</a>
            </div>
            <p className="text-sm text-[#6B6B6B] max-w-2xl mx-auto">
              Not a job. No guarantees. Results depend on effort and participation.
            </p>
            <p className="text-xs text-[#6B6B6B]">
              The system rewards action, not guarantees.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FoundersLaunchPage;
